"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from "react";
import { ScreenShare } from "lucide-react";
import { useIdentity } from "@/lib/providers/identity";
import { useChatSurfaceFlags } from "../model/use-chat-surface-flags";
import { useMeetingStore } from "../model/meeting-store";
import { meetingRoom } from "../model/meeting-room";
import { useSpaceMeetings } from "../model/use-space-meetings";
import { useGroupMembers } from "../model";
import { resolveRoster } from "../model/meeting-roster";
import { LiveDot } from "./live-dot";
import { MeetingControls } from "./meeting-controls";
import { StageShell } from "./stage-shell";
import { StageTile } from "./stage-tile";
import { PanelFace } from "./panel-face";
import { GuestKnockNotice } from "./guest-knock-notice";

/**
 * MeetingStage — the in-app meeting UI. Mounted once app-wide (next to CallBridge)
 * so a meeting survives navigation.
 *
 * TWO views, one session — the panel ⇄ expanded frame lives in StageShell (shared
 * with the direct-call stage); this component supplies the meeting-specific
 * content: roster from the meetings store, share video sink, host controls, and
 * the tab-close leave beacon.
 */
export function MeetingStage() {
  const $ = _c(91);
  const phase = useMeetingStore(_temp);
  const expanded = useMeetingStore(_temp2);
  const conversationId = useMeetingStore(_temp3);
  const meetingId = useMeetingStore(_temp4);
  const title = useMeetingStore(_temp5);
  const isMuted = useMeetingStore(_temp6);
  const listenOnly = useMeetingStore(_temp7);
  const participants = useMeetingStore(_temp8);
  const isSharing = useMeetingStore(_temp9);
  const shareTileId = useMeetingStore(_temp10);
  const sharePresenterId = useMeetingStore(_temp11);
  const patch = useMeetingStore(_temp12);
  const active = phase === "joining" || phase === "live";
  const {
    meetings,
    leaveMeeting,
    endMeeting
  } = useSpaceMeetings(conversationId, active);
  const {
    members
  } = useGroupMembers(conversationId, active && conversationId != null);
  let t0;
  if ($[0] !== meetingId || $[1] !== meetings) {
    t0 = meetings.find(m => m.id === meetingId)?.isHost ?? false;
    $[0] = meetingId;
    $[1] = meetings;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isHost = t0;
  let t1;
  if ($[3] !== members || $[4] !== participants) {
    t1 = resolveRoster(participants, members);
    $[3] = members;
    $[4] = participants;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const faces = t1;
  const shareActive = shareTileId != null;
  let t2;
  if ($[6] !== members || $[7] !== sharePresenterId) {
    let t3;
    if ($[9] !== sharePresenterId) {
      t3 = m_0 => m_0.userId === sharePresenterId;
      $[9] = sharePresenterId;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = members.find(t3);
    $[6] = members;
    $[7] = sharePresenterId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const presenter = t2;
  const iAmPresenter = isSharing || presenter?.isMe === true;
  const presenterName = presenter?.isMe ? "You" : presenter?.name ?? "Someone";
  const audioRef = useRef(null);
  let t3;
  let t4;
  if ($[11] !== active) {
    t3 = () => {
      if (!active) {
        return;
      }
      meetingRoom.attachAudio(audioRef.current);
      return _temp13;
    };
    t4 = [active];
    $[11] = active;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useEffect(_temp15, t5);
  if (!active) {
    return null;
  }
  let t6;
  if ($[15] !== leaveMeeting || $[16] !== meetingId) {
    t6 = () => void (meetingId && leaveMeeting(meetingId));
    $[15] = leaveMeeting;
    $[16] = meetingId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const onLeave = t6;
  let t7;
  if ($[18] !== endMeeting || $[19] !== meetingId) {
    t7 = () => void (meetingId && endMeeting(meetingId));
    $[18] = endMeeting;
    $[19] = meetingId;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const onEnd = t7;
  const meta = phase === "joining" ? "connecting\u2026" : `${participants.length} ${participants.length === 1 ? "person" : "people"}${listenOnly ? " \xB7 listen-only" : ""}`;
  const t8 = shareActive && !iAmPresenter;
  let t9;
  if ($[21] !== isHost || $[22] !== isMuted || $[23] !== isSharing || $[24] !== listenOnly || $[25] !== onEnd || $[26] !== onLeave || $[27] !== t8) {
    t9 = <MeetingControls isMuted={isMuted} listenOnly={listenOnly} isHost={isHost} isSharing={isSharing} shareBlocked={t8} onLeave={onLeave} onEnd={onEnd} />;
    $[21] = isHost;
    $[22] = isMuted;
    $[23] = isSharing;
    $[24] = listenOnly;
    $[25] = onEnd;
    $[26] = onLeave;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const controls = t9;
  let t10;
  if ($[29] !== patch) {
    t10 = next => patch({
      expanded: next
    });
    $[29] = patch;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== phase) {
    t11 = <LiveDot phase={phase} />;
    $[31] = phase;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const t12 = title ?? "Meeting";
  let t13;
  if ($[33] !== t12) {
    t13 = <h2 className="truncate text-sm font-semibold">{t12}</h2>;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = phase === "joining" ? "connecting\u2026" : `${participants.length} in the room${listenOnly ? " \xB7 listen-only" : ""}`;
  let t15;
  if ($[35] !== t14) {
    t15 = <p className="text-xs text-white/50">{t14}</p>;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t15) {
    t16 = <div className="min-w-0 flex-1">{t13}{t15}</div>;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t11 || $[41] !== t16) {
    t17 = <>{t11}{t16}</>;
    $[40] = t11;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== phase) {
    t18 = <LiveDot phase={phase} />;
    $[43] = phase;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = title ?? "Meeting";
  let t20;
  if ($[45] !== t19) {
    t20 = <p className="truncate text-sm font-semibold">{t19}</p>;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== meta) {
    t21 = <p className="text-[11px] text-white/50">{meta}</p>;
    $[47] = meta;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t20 || $[50] !== t21) {
    t22 = <div className="min-w-0 flex-1">{t20}{t21}</div>;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t18 || $[53] !== t22) {
    t23 = <>{t18}{t22}</>;
    $[52] = t18;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== active || $[56] !== meetingId) {
    t24 = <GuestKnockNotice meetingId={meetingId} active={active} />;
    $[55] = active;
    $[56] = meetingId;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== faces || $[59] !== iAmPresenter || $[60] !== phase || $[61] !== shareActive) {
    t25 = shareActive && !iAmPresenter ? <div className="flex h-full w-full flex-col gap-3"><video ref={_temp16} autoPlay={true} playsInline={true} muted={true} data-testid="meeting-share-video" className="min-h-0 w-full flex-1 rounded-xl bg-black object-contain" /><div className="flex shrink-0 flex-wrap items-center justify-center gap-3">{faces.map(_temp17)}</div></div> : <div className="flex flex-col items-center gap-4">{iAmPresenter ? <p className="flex items-center gap-2 text-sm text-white/60"><ScreenShare className="h-4 w-4" aria-hidden={true} /> You’re sharing your screen</p> : null}{faces.length === 0 ? <p className="text-sm text-white/40">{phase === "joining" ? "Connecting to the room\u2026" : "Waiting for people to join\u2026"}</p> : <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">{faces.map(_temp18)}</div>}</div>;
    $[58] = faces;
    $[59] = iAmPresenter;
    $[60] = phase;
    $[61] = shareActive;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t24 || $[64] !== t25) {
    t26 = <div className="flex h-full w-full flex-col gap-3">{t24}{t25}</div>;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== active || $[67] !== meetingId) {
    t27 = <GuestKnockNotice meetingId={meetingId} active={active} />;
    $[66] = active;
    $[67] = meetingId;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== patch || $[70] !== presenterName || $[71] !== shareActive) {
    t28 = shareActive ? <button type="button" onClick={() => patch({
      expanded: true
    })} className="flex items-center gap-1.5 rounded-lg bg-[#5651ff]/20 px-2.5 py-1.5 text-left text-[11px] font-medium text-[#c8c5ff] transition-colors hover:bg-[#5651ff]/30 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80"><ScreenShare className="h-3.5 w-3.5 shrink-0" aria-hidden={true} /><span className="truncate">{presenterName} sharing screen — expand to view</span></button> : null;
    $[69] = patch;
    $[70] = presenterName;
    $[71] = shareActive;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== faces || $[74] !== phase) {
    t29 = faces.length === 0 ? <span className="py-1 text-xs text-white/40">{phase === "joining" ? "Connecting\u2026" : "Waiting for people\u2026"}</span> : faces.map(_temp19);
    $[73] = faces;
    $[74] = phase;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t29) {
    t30 = <div className="flex flex-wrap gap-x-3 gap-y-2">{t29}</div>;
    $[76] = t29;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t27 || $[79] !== t28 || $[80] !== t30) {
    t31 = <>{t27}{t28}{t30}</>;
    $[78] = t27;
    $[79] = t28;
    $[80] = t30;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== controls || $[83] !== expanded || $[84] !== phase || $[85] !== t10 || $[86] !== t17 || $[87] !== t23 || $[88] !== t26 || $[89] !== t31) {
    t32 = <StageShell testId="meeting-stage" phase={phase} ariaLabel="Meeting" expanded={expanded} onExpandedChange={t10} expandTestId="meeting-expand" collapseTestId="meeting-collapse" audioRef={audioRef} expandedHeader={t17} panelHeader={t23} expandedBody={t26} panelBody={t31} controls={controls} />;
    $[82] = controls;
    $[83] = expanded;
    $[84] = phase;
    $[85] = t10;
    $[86] = t17;
    $[87] = t23;
    $[88] = t26;
    $[89] = t31;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  return t32;
}

/**
 * Mounts the stage once app-wide, gated on the chat flag like the other chat
 * bridges. The stage self-gates on meeting phase, so orgs without chat never
 * render it.
 */
function _temp19(f_1) {
  return <PanelFace key={f_1.key} face={f_1} />;
}
function _temp18(f_0) {
  return <StageTile key={f_0.key} face={f_0} />;
}
function _temp17(f) {
  return <PanelFace key={f.key} face={f} />;
}
function _temp16(el) {
  return meetingRoom.attachShareVideo(el);
}
function _temp15() {
  const onHide = _temp14;
  window.addEventListener("pagehide", onHide);
  return () => window.removeEventListener("pagehide", onHide);
}
function _temp14() {
  const {
    meetingId: mid,
    phase: p
  } = useMeetingStore.getState();
  if (!mid || p !== "live" && p !== "joining") {
    return;
  }
  const blob = new Blob([JSON.stringify({
    meetingId: mid
  })], {
    type: "application/json"
  });
  navigator.sendBeacon("/api/chat/meetings/leave", blob);
}
function _temp13() {
  return meetingRoom.attachAudio(null);
}
function _temp12(s_10) {
  return s_10.patch;
}
function _temp11(s_9) {
  return s_9.sharePresenterId;
}
function _temp10(s_8) {
  return s_8.shareTileId;
}
function _temp9(s_7) {
  return s_7.isSharing;
}
function _temp8(s_6) {
  return s_6.participants;
}
function _temp7(s_5) {
  return s_5.listenOnly;
}
function _temp6(s_4) {
  return s_4.isMuted;
}
function _temp5(s_3) {
  return s_3.title;
}
function _temp4(s_2) {
  return s_2.meetingId;
}
function _temp3(s_1) {
  return s_1.conversationId;
}
function _temp2(s_0) {
  return s_0.expanded;
}
function _temp(s) {
  return s.phase;
}
export function MeetingStageBridge() {
  const $ = _c(1);
  const {
    identity
  } = useIdentity();
  const {
    meetingsEnabled
  } = useChatSurfaceFlags();
  if (!identity || !meetingsEnabled) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MeetingStage />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}